import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema } from '../schemas';

/**
 * Root storage contract - routes at the service root (no prefix).
 *
 * IMPORTANT: Specific paths must come BEFORE parameterized paths.
 * /images/*, /bucket/*, /translations BEFORE /:projectId/*.
 *
 * `GET /images/logo?bucket=&assetPath=` and `GET /:folderName?bucket=` used to
 * live here: both took the S3 bucket from the caller with no role gate, so any
 * authenticated user could read any object in any bucket the service can reach
 * — no scope the route declared could constrain that. Neither had a caller in
 * any repo, so they are deleted rather than gated. Every real asset read is
 * project- or organization-scoped (`/projects/:projectId/logo`,
 * `/organizations/:organizationId/logo`), or a fixed bucket (`/images/icon`).
 *
 * Five more are gone for a related reason. `POST /images/building`,
 * `POST /images/logo`, `POST /images/floors`, and `POST|GET
 * /:projectId/projectImage` each passed the RAW projectId to
 * `StorageService` as a bucket name, while every read counterpart derives
 * `${environment}-project-<projectId>` via
 * `ProjectStorageService.getProjectBucketName`. Nothing written through them was
 * ever readable through the API, and the first three additionally carried their
 * project in a multipart BODY. They had no caller in either repo, nor in the
 * devops/trusstorutils infrastructure repos; the real upload and read surface is
 * `projects.contract.ts` (`/projects/:projectId/buildingView`, `/logo`,
 * `/floorView`, `/projectImage`), which the backoffice and shared clients
 * already use.
 *
 * The rule they violated outlives them: never accept a bucket name from the
 * request, and never DERIVE one from a value the request supplies without
 * validating it as a project first.
 */
export const rootContract = defineContract(
	'storage',
	{
		getIcon: route({
			method: 'GET',
			path: '/images/icon',
			query: z.object({ assetPath: requiredStringSchema }),
			response: z.any(),
			operationId: 'getIcon',
			tags: ['Root Storage'],
			summary: 'Get base64 icon from trusstor-icons bucket',
		}),
		createBucket: route({
			method: 'POST',
			path: '/bucket/:bucketName',
			params: z.object({ bucketName: requiredStringSchema }),
			response: z.string(),
			operationId: 'createBucket',
			tags: ['Root Storage'],
			summary: 'Create an S3 bucket',
		}),
		deleteBucket: route({
			method: 'DELETE',
			path: '/bucket/:bucketName',
			params: z.object({ bucketName: requiredStringSchema }),
			response: z.string(),
			operationId: 'deleteBucket',
			tags: ['Root Storage'],
			summary: 'Delete an S3 bucket',
		}),
	},
	{ root: true }
);
